// KpiHeroBand — standard top-of-page KPI band. Lays out KpiCard tiles in a
// responsive grid with an eyebrow kicker and an optional data-freshness chip.
// Thin composition over the existing KpiCard; no new chart.
//
// Freshness contract (AC-TRUST-1): `asOf` renders only when it is a real
// timestamp (Date, epoch number, or ISO datetime string). Bare claim strings
// ("Live", "Filtered", …) are ignored — a freshness label must be backed by an
// actual data-load or source timestamp. Use `context` for descriptive,
// non-freshness labels (e.g. "3 in pipeline"); it renders as a neutral chip.

import { KpiCard } from '../inspectors/KpiCard.jsx';
import './redesign.css';

// Standard tone mapping so every page derives KPI tone the same way.
// `invert` = lower is better (e.g. overruns, slip days).
export function deriveKpiTone({ value, target, atRisk = false, invert = false } = {}) {
  if (atRisk) return 'brick';
  if (typeof value === 'number' && typeof target === 'number' && target !== 0) {
    const ratio = value / target;
    const good = invert ? ratio <= 1 : ratio >= 1;
    if (good) return 'sage';
    const near = invert ? ratio <= 1.1 : ratio >= 0.9;
    return near ? 'amber' : 'brick';
  }
  return 'slate';
}

// Strings must look like an ISO datetime — this is what keeps "Live"/"Filtered"
// style claims from ever rendering as freshness.
export function parseAsOfTimestamp(value) {
  if (value == null) return null;
  let d = null;
  if (value instanceof Date) d = value;
  else if (typeof value === 'number') d = new Date(value);
  else if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value))
    d = new Date(value);
  if (!d || Number.isNaN(d.getTime())) return null;
  return d;
}

function formatAsOf(d) {
  const sameDay = d.toDateString() === new Date().toDateString();
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (sameDay) return `As of ${time}`;
  const date = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  return `As of ${date}, ${time}`;
}

export function KpiHeroBand({
  eyebrow,
  asOf,
  context,
  items = [],
  columns,
  className = '',
  stagger = true,
}) {
  const cols = columns || Math.min(4, Math.max(1, items.length));
  const asOfDate = parseAsOfTimestamp(asOf);
  const contextLabel = typeof context === 'string' && context.trim() ? context : null;
  return (
    <section className={`kpi-hero-band ${className}`} aria-label={eyebrow || 'Key metrics'}>
      {eyebrow || asOfDate || contextLabel ? (
        <div className="kpi-hero-band__head">
          {eyebrow ? <span className="eyebrow">{eyebrow}</span> : <span />}
          {asOfDate ? (
            <span className="chip sm as-of" title={asOfDate.toLocaleString()}>
              {formatAsOf(asOfDate)}
            </span>
          ) : contextLabel ? (
            <span className="chip outline sm">{contextLabel}</span>
          ) : null}
        </div>
      ) : null}
      <div
        className={`kpi-hero-band__grid ${stagger ? 'stagger-reveal' : ''}`}
        style={{ '--kpi-cols': cols }}
      >
        {items.map((it, i) => (
          <KpiCard key={it.id || it.label || i} surface={it.surface || 'hero'} {...it} />
        ))}
      </div>
    </section>
  );
}
